import { toast } from "sonner";

export type ExportRow = {
  geographyCode: string;
  name: string;
  metricId: string;
  metricLabel: string;
  value: number | null;
  unit: string;
  qualityFlag: string | null;
};

/**
 * Escapes CSV field strings according to RFC 4180
 */
export function escapeCSVField(field: string | number | null | undefined): string {
  if (field === null || field === undefined) return "";
  const str = String(field);
  if (str.includes(",") || str.includes('"') || str.includes("\n")) {
    return `"${str.replaceAll('"', '""')}"`;
  }
  return str;
}

/**
 * Converts data rows into a formatted CSV string and triggers browser file download
 */
export function downloadCSV(filename: string, headers: string[], rows: (string | number | null | undefined)[][]): void {
  const csvLines = [
    headers.map((h) => escapeCSVField(h)).join(","),
    ...rows.map((row) => row.map((r) => escapeCSVField(r)).join(",")),
  ];

  const csvContent = csvLines.join("\r\n");
  const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
  const url = URL.createObjectURL(blob);

  const link = document.createElement("a");
  link.setAttribute("href", url);
  link.setAttribute("download", filename.endsWith(".csv") ? filename : `${filename}.csv`);
  document.body.append(link);
  link.click();
  link.remove();
  URL.revokeObjectURL(url);

  toast.success("CSV Downloaded", { description: `Exported ${rows.length} rows to ${filename}` });
}

/**
 * Downloads a GeoJSON object with merged observation values as a .geojson file
 */
export function downloadGeoJSON(filename: string, geojson: unknown): void {
  const jsonString = JSON.stringify(geojson, null, 2);
  const blob = new Blob([jsonString], { type: "application/geo+json;charset=utf-8;" });
  const url = URL.createObjectURL(blob);

  const link = document.createElement("a");
  link.setAttribute("href", url);
  link.setAttribute("download", filename.endsWith(".geojson") ? filename : `${filename}.geojson`);
  document.body.append(link);
  link.click();
  link.remove();
  URL.revokeObjectURL(url);

  toast.success("GeoJSON Downloaded", { description: `Saved boundaries and observations to ${filename}` });
}

/**
 * Copies a JSON string or object payload directly to the clipboard
 */
export async function copyJSONToClipboard(data: unknown): Promise<void> {
  try {
    const jsonString = JSON.stringify(data, null, 2);
    await navigator.clipboard.writeText(jsonString);
    toast.success("Copied to Clipboard", { description: "Raw JSON payload copied to clipboard" });
  } catch (error) {
    console.error("Clipboard copy failed", error);
    toast.error("Copy Failed", { description: "Unable to write to clipboard" });
  }
}
